import type { components, operations, paths } from "@repo/api-types/merchant"
import type { ReservationPriceCapability, ReservationReferencePrice } from "./presentation"
import { v4 as uuidv4 } from "uuid"
import { createTypedClient } from "@/api/typed/client"
import { getConfig } from "@/api/utils"
import { service2 as service } from "@/utils/service"

const baseOptions = getConfig({ prefix: "/teashop_merchant" })
const typedClient = createTypedClient<paths>({
  service,
  defaults: {
    baseURL: baseOptions.baseURL,
    headers: baseOptions.headers,
    timeout: baseOptions.timeout,
  },
})

export type ReservationStatus =
  | "pending"
  | "confirmed"
  | "arrived"
  | "completed"
  | "rejected"
  | "cancelled"
  | "expired"
export type ReservationSource = "ONLINE" | "STORE"
export type ReservationNotificationMode = "NONE" | "SMS"

export type ReservationDetail = components["schemas"]["ReservationDetailDTO"]
export type ReservationPage = components["schemas"]["ReservationPagedResponseDTO"]
export type ReservationWorkbench = components["schemas"]["ReservationWorkbenchResponse"]
export type ReservationPendingSummary = components["schemas"]["ReservationPendingSummaryResponse"]
export type ReservationWorkbenchOrder = components["schemas"]["ReservationWorkbenchOrderResponse"]
export type ReservationActionContext = ReservationWorkbenchOrder
export type ReservationWorkbenchTable = components["schemas"]["ReservationWorkbenchTableResponse"]
export type ReservationConfig = components["schemas"]["ReservationConfigResponse"]
export type ReservationTableProfile = components["schemas"]["ReservationTableProfileResponse"] & ReservationPriceCapability
export type ReservationImage = components["schemas"]["ReservationImageResponse"]
export type ReservationDetailReportPageQuery =
  components["schemas"]["ReservationDetailReportPageQueryRequest"]
export type ReservationDetailReportPage =
  components["schemas"]["ReservationDetailReportPageResponse"]
export type ReservationDetailReportRecord =
  components["schemas"]["ReservationDetailReportRecord"]
export type ReservationConsumptionOrders =
  components["schemas"]["ReservationConsumptionOrdersResponse"]
export type ReservationConsumptionOrder =
  components["schemas"]["ReservationConsumptionOrder"]
export type ReservationDetailReportExportRequest =
  components["schemas"]["ReservationDetailReportExportRequest"]
export type ReservationDetailReportExport =
  components["schemas"]["ReservationDetailReportExportResponse"]
export type ReservationEmployeeOptionQuery =
  components["schemas"]["ReservationEmployeeOptionQueryRequest"]
export type ReservationEmployeeOptionPage =
  components["schemas"]["ReservationEmployeeOptionPageResponse"]
export type ReservationEmployeeOption = components["schemas"]["EmployeeItem"]
export type ReservationEmployeeUpdateRequest =
  components["schemas"]["ReservationEmployeeUpdateRequest"]
export type ReservationEmployeeCommissionQuery =
  components["schemas"]["ReservationEmployeeCommissionQueryRequest"]
export type ReservationEmployeeCommissionSummary =
  components["schemas"]["ReservationEmployeeCommissionSummary"]
export type ReservationEmployeeCommissionRecord =
  components["schemas"]["ReservationEmployeeCommissionRecord"]
export type ReservationEmployeeCommissionPage =
  components["schemas"]["ReservationEmployeeCommissionPageResponse"]
export type ReservationEmployeeCommissionDetailQuery =
  components["schemas"]["ReservationEmployeeCommissionDetailQueryRequest"]
export type ReservationEmployeeCommissionDetailRecord =
  components["schemas"]["ReservationEmployeeCommissionDetailRecord"]
export type ReservationEmployeeCommissionDetailPage =
  components["schemas"]["ReservationEmployeeCommissionDetailPageResponse"]
export type ReservationAdjacentItem = components["schemas"]["ReservationAdjacentItemResponse"]
export type ReservationAdjacentQueryResponse =
  components["schemas"]["ReservationAdjacentQueryResponse"]

export type ReservationWorkbenchQuery =
  NonNullable<operations["AdminReservationWorkbench_query"]["parameters"]["query"]>
export type ReservationActionContextQuery =
  NonNullable<operations["AdminReservationWorkbench_actionContext"]["parameters"]["query"]>
export type AdminReservationListQuery =
  NonNullable<operations["AdminReservation_list"]["parameters"]["query"]>
export type ReservationAdjacentQuery =
  NonNullable<operations["AdminReservation_adjacent"]["parameters"]["query"]>
export type StoreReservationCreateRequest = components["schemas"]["StoreReservationCreateRequest"]
export type StoreReservationV2CreateRequest = components["schemas"]["StoreReservationV2CreateRequest"]
export type StoreReservationCreateResult = components["schemas"]["StoreReservationCreateResponse"]
export type StoreReservationCapabilities = components["schemas"]["StoreReservationCapabilitiesResponse"]
export type StoreReservationSmsSummary = components["schemas"]["ReservationSmsSummaryResponse"]
export type ReservationUpdateRequest = components["schemas"]["ReservationUpdateRequest"]
export type ConfirmReservationRequest = components["schemas"]["ConfirmReservationCommand"]
export type ReservationActionRequest = components["schemas"]["CancelReservationCommand"]
export type ReservationArrivalRequest = components["schemas"]["ReservationArrivalRequest"]
export type ReservationFulfillmentItems = components["schemas"]["ReservationFulfillmentItemsResponse"]
export type ReservationConfigUpdateRequest = components["schemas"]["ReservationConfigUpdateRequest"]
export type ReservationTableProfileUpdateRequest =
  components["schemas"]["ReservationTableProfileUpdateRequest"] & { referencePrice?: ReservationReferencePrice }

interface ReservationEnvelope<T> {
  code?: number
  data?: T
  msg?: string
}

interface ReservationHttpResponse<T> {
  data: ReservationEnvelope<T>
}

export interface ReservationCreateOptions {
  /** 一次用户动作生成一次；网络重试必须复用同一个 key。 */
  idempotencyKey: string
}

export class ReservationApiError extends Error {
  code?: number
  status?: number

  constructor(message: string, options: { code?: number, status?: number } = {}) {
    super(message)
    this.name = "ReservationApiError"
    this.code = options.code
    this.status = options.status
  }
}

export function createReservationIdempotencyKey() {
  return uuidv4()
}

export function isReservationConflictError(error: unknown) {
  return error instanceof ReservationApiError
    && [47603, 47604, 47609, 47612].includes(error.code ?? -1)
}

function requestConfig(options?: ReservationCreateOptions) {
  const idempotencyKey = options?.idempotencyKey?.trim()
  if (options && !idempotencyKey) {
    throw new ReservationApiError("缺少 Idempotency-Key")
  }
  return {
    headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
    skipBusinessCodeCheck: true,
    skipGlobalErrorToast: true,
  }
}

function normalizeReservationError(error: unknown): ReservationApiError {
  if (error instanceof ReservationApiError) return error

  const candidate = error as {
    message?: string
    response?: { data?: ReservationEnvelope<unknown>, status?: number }
  }
  const code = candidate?.response?.data?.code
  const message = candidate?.response?.data?.msg || candidate?.message || "预订请求失败"
  return new ReservationApiError(message, {
    code,
    status: candidate?.response?.status,
  })
}

async function unwrap<T>(promise: Promise<unknown>, label: string): Promise<T> {
  try {
    const response = await promise as ReservationHttpResponse<T>
    const envelope = response?.data
    if (!envelope || envelope.code !== 0) {
      throw new ReservationApiError(envelope?.msg || `${label}响应异常`, {
        code: envelope?.code,
      })
    }
    if (envelope.data == null) {
      throw new ReservationApiError(`${label}响应缺少数据`)
    }
    return envelope.data
  } catch (error) {
    throw normalizeReservationError(error)
  }
}

export function getReservationWorkbench(query: ReservationWorkbenchQuery) {
  return unwrap<ReservationWorkbench>(
    typedClient.get("/admin/reservation-workbench", {
      query,
      config: requestConfig(),
    }),
    "预订工作台",
  )
}

export function getReservationPendingSummary(shopId: number) {
  return unwrap<ReservationPendingSummary>(
    typedClient.get("/admin/reservation-workbench/pending-summary", {
      query: { shopId },
      config: requestConfig(),
    }),
    "待确认预订汇总",
  )
}

export function getReservationActionContext(query: ReservationActionContextQuery) {
  return unwrap<ReservationActionContext>(
    typedClient.get("/admin/reservation-workbench/action-context", {
      query,
      config: requestConfig(),
    }),
    "预订操作上下文",
  )
}

export function queryReservationDetailReport(body: ReservationDetailReportPageQuery) {
  return unwrap<ReservationDetailReportPage>(
    typedClient.post("/admin/reservation-reports/details/query", body, {
      config: requestConfig(),
    }),
    "预订明细",
  )
}

export function queryReservationReportEmployeeOptions(body: ReservationDetailReportPageQuery) {
  return unwrap<Array<components["schemas"]["ReservationReportEmployeeOptionResponse"]>>(
    typedClient.post("/admin/reservation-reports/details/employee-options", body, {
      config: requestConfig(),
    }),
    "报表员工候选",
  )
}

export function getReservationConsumptionOrders(
  source: ReservationSource,
  sourceId: number,
  reservationRef?: string,
) {
  if (reservationRef) {
    return unwrap<ReservationConsumptionOrders>(
      typedClient.get("/admin/reservation-reports/details/by-ref/{reservationRef}/orders", {
        pathParams: { reservationRef },
        config: requestConfig(),
      }),
      "预订消费订单明细",
    )
  }
  return unwrap<ReservationConsumptionOrders>(
    typedClient.get("/admin/reservation-reports/details/{source}/{sourceId}/orders", {
      pathParams: { source, sourceId },
      config: requestConfig(),
    }),
    "预订消费订单明细",
  )
}

export function exportReservationDetailReport(body: ReservationDetailReportExportRequest) {
  return unwrap<ReservationDetailReportExport>(
    typedClient.post("/admin/reservation-reports/details/export", body, {
      config: requestConfig(),
    }),
    "导出预订明细",
  )
}

export function queryReservationEmployeeOptions(body: ReservationEmployeeOptionQuery) {
  return unwrap<ReservationEmployeeOptionPage>(
    typedClient.post("/admin/reservation-employee-options/query", body, {
      config: requestConfig(),
    }),
    "预订员工候选",
  )
}

export function updateReservationEmployee(
  source: ReservationSource,
  sourceId: number,
  body: ReservationEmployeeUpdateRequest,
  reservationRef?: string,
) {
  if (reservationRef) {
    return unwrap<ReservationWorkbenchOrder>(
      typedClient.patch("/admin/reservation-workbench/by-ref/{reservationRef}/employee", body, {
        pathParams: { reservationRef },
        config: requestConfig(),
      }),
      "修改预订员工",
    )
  }
  return unwrap<ReservationWorkbenchOrder>(
    typedClient.patch("/admin/reservation-workbench/orders/{source}/{sourceId}/employee", body, {
      pathParams: { source, sourceId },
      config: requestConfig(),
    }),
    "修改预订员工",
  )
}

/** 旧独立工作台兼容读取；Smart 工作台应使用 getReservationWorkbench。 */
export function listAdminReservations(query: AdminReservationListQuery = {}) {
  return unwrap<ReservationPage>(
    typedClient.get("/admin/reservations", {
      query,
      config: requestConfig(),
    }),
    "预订列表",
  )
}

export function getAdminReservationDetail(reservationId: number) {
  return unwrap<ReservationDetail>(
    typedClient.get("/admin/reservations/{reservationId}", {
      pathParams: { reservationId },
      config: requestConfig(),
    }),
    "预订详情",
  )
}

export function getReservationAdjacent(
  reservationId: number,
  query: ReservationAdjacentQuery,
) {
  return unwrap<ReservationAdjacentQueryResponse>(
    typedClient.get("/admin/reservations/{reservationId}/adjacent", {
      pathParams: { reservationId },
      query,
      config: requestConfig(),
    }),
    "相邻预订",
  )
}

export function createStoreReservation(
  body: StoreReservationCreateRequest,
  options: ReservationCreateOptions,
) {
  return unwrap<ReservationWorkbenchOrder>(
    typedClient.post("/admin/store-reservations", body, {
      config: requestConfig(options),
    }),
    "创建店订",
  )
}

export function createStoreReservationV2(body: StoreReservationV2CreateRequest, options: ReservationCreateOptions) {
  return unwrap<StoreReservationCreateResult>(typedClient.post("/admin/v2/store-reservations", body, {
    config: requestConfig(options),
  }), "创建预订")
}

export function getStoreReservationCreateResult(shopId: number, requestId: string) {
  return unwrap<components["schemas"]["StoreReservationCreateResultResponse"]>(
    typedClient.get("/admin/v2/store-reservations/create-result", {
      query: { shopId, requestId },
      config: requestConfig(),
    }),
    "查询创建结果",
  )
}

export function getStoreReservationCapabilities(shopId: number) {
  return unwrap<StoreReservationCapabilities>(typedClient.get("/admin/v2/store-reservations/capabilities", {
    query: { shopId },
    config: requestConfig(),
  }), "查询预订通知能力")
}

export function closeStoreReservationCreateRequest(shopId: number, requestId: string) {
  return unwrap<components["schemas"]["StoreReservationCreateResultResponse"]>(
    typedClient.post("/admin/v2/store-reservations/create-request-closures", { shopId, requestId }, {
      config: requestConfig(),
    }),
    "核对并结束创建请求",
  )
}

export function getStoreReservationCreationNotification(reservationId: number) {
  return unwrap<StoreReservationSmsSummary>(typedClient.get("/admin/v2/store-reservations/{reservationId}/creation-notification", {
    pathParams: { reservationId },
    config: requestConfig(),
  }), "查询短信状态")
}

export function retryStoreReservationCreationNotification(reservationId: number, requestNo: string, reason: string) {
  return unwrap<StoreReservationSmsSummary>(typedClient.post(
    "/admin/v2/store-reservations/{reservationId}/creation-notification/retry-attempts",
    { requestNo, reason },
    { pathParams: { reservationId }, config: requestConfig() },
  ), "重试预订短信")
}

export function cancelStoreReservation(
  sourceId: number,
  body: ReservationActionRequest,
  reservationRef?: string,
) {
  if (reservationRef) {
    return unwrap<ReservationWorkbenchOrder>(
      typedClient.post("/admin/store-reservations/by-ref/{reservationRef}/cancel", body, {
        pathParams: { reservationRef },
        config: requestConfig(),
      }),
      "取消预订",
    )
  }
  return unwrap<ReservationWorkbenchOrder>(
    typedClient.post("/admin/store-reservations/{sourceId}/cancel", body, {
      pathParams: { sourceId },
      config: requestConfig(),
    }),
    "取消店订",
  )
}

export function updateReservation(
  reservationId: number,
  body: ReservationUpdateRequest,
) {
  return unwrap<ReservationDetail>(
    typedClient.patch("/admin/reservations/{reservationId}", body, {
      pathParams: { reservationId },
      config: requestConfig(),
    }),
    "修改预订",
  )
}

export function confirmReservation(
  reservationId: number,
  body: ConfirmReservationRequest,
) {
  return unwrap<ReservationDetail>(
    typedClient.post("/admin/reservations/{reservationId}/confirm", body, {
      pathParams: { reservationId },
      config: requestConfig(),
    }),
    "确认预订",
  )
}

export function rejectReservation(
  reservationId: number,
  body: ReservationActionRequest,
) {
  return unwrap<ReservationDetail>(
    typedClient.post("/admin/reservations/{reservationId}/reject", body, {
      pathParams: { reservationId },
      config: requestConfig(),
    }),
    "拒绝预订",
  )
}

export function cancelReservation(
  reservationId: number,
  body: ReservationActionRequest,
) {
  return unwrap<ReservationDetail>(
    typedClient.post("/admin/reservations/{reservationId}/cancel", body, {
      pathParams: { reservationId },
      config: requestConfig(),
    }),
    "取消预订",
  )
}

export function getReservationFulfillmentItems(reservationId: number) {
  return unwrap<ReservationFulfillmentItems>(
    typedClient.get("/cashier/reservations/{reservationId}/items", {
      pathParams: { reservationId },
      config: requestConfig(),
    }),
    "读取预订包间",
  )
}

export function getReservationEditableItems(reservationId: number) {
  return unwrap<ReservationFulfillmentItems>(
    typedClient.get("/admin/reservations/{reservationId}/items", {
      pathParams: { reservationId },
      config: requestConfig(),
    }),
    "读取可修改预订包间",
  )
}

export function arriveReservation(
  reservationId: number,
  body: ReservationArrivalRequest,
) {
  return unwrap<ReservationDetail>(
    typedClient.post("/cashier/reservations/{reservationId}/arrive", body, {
      pathParams: { reservationId },
      config: requestConfig(),
    }),
    "到店核销",
  )
}

export function getReservationConfig() {
  return unwrap<ReservationConfig>(
    typedClient.get("/admin/reservation-config", {
      config: requestConfig(),
    }),
    "预订配置",
  )
}

export function updateReservationConfig(
  body: ReservationConfigUpdateRequest,
) {
  return unwrap<ReservationConfig>(
    typedClient.put("/admin/reservation-config", body, {
      config: requestConfig(),
    }),
    "更新预订配置",
  )
}

export function listReservationTableProfiles(shopId: number) {
  return unwrap<ReservationTableProfile[]>(
    typedClient.get("/admin/reservation-table-profiles", {
      query: { shopId },
      config: requestConfig(),
    }),
    "桌台预订资料",
  )
}

export function uploadReservationTableProfileImage(file: File) {
  const formData = new FormData()
  formData.append("file", file)
  const config = requestConfig()
  return unwrap<ReservationImage>(
    service.post("/admin/reservation-table-profiles/images", formData, {
      ...baseOptions,
      ...config,
      timeout: 120000,
      headers: {
        ...baseOptions.headers,
        ...config.headers,
        "Content-Type": "multipart/form-data",
      },
    }),
    "上传桌台预订图片",
  )
}

export function updateReservationTableProfile(
  tableId: number,
  body: ReservationTableProfileUpdateRequest,
) {
  return unwrap<ReservationTableProfile>(
    typedClient.put("/admin/reservation-table-profiles/{tableId}", body, {
      pathParams: { tableId },
      config: requestConfig(),
    }),
    "更新桌台预订资料",
  )
}

export function queryReservationEmployeeCommissions(body: ReservationEmployeeCommissionQuery) {
  return unwrap<ReservationEmployeeCommissionPage>(
    typedClient.post("/admin/reservation-reports/employees/query", body, {
      config: requestConfig(),
    }),
    "员工预订提成",
  )
}

export function queryReservationEmployeeCommissionDetails(
  employeeCorpUserId: number,
  body: ReservationEmployeeCommissionDetailQuery,
) {
  return unwrap<ReservationEmployeeCommissionDetailPage>(
    typedClient.post(
      "/admin/reservation-reports/employees/{employeeCorpUserId}/details/query",
      body,
      {
        pathParams: { employeeCorpUserId },
        config: requestConfig(),
      },
    ),
    "员工预订提成明细",
  )
}
